import { KeyboardEvent, useState } from 'react';
import { UseFormGetValues, UseFormReset, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { useFocusInputFunctionality } from 'core/hooks';
import { IAutocompleteOption } from 'core/types';
import { isEnterKeyPress, isTabKeyPress } from 'core/utils';
import { useComponentTemplateActions } from 'elements/hooks';
import { PartNumberHelper } from 'parts/helpers';
import { IManufacturer, IPart, IUom } from 'parts/types';

import { IAddPartLineFormData } from './types';

interface IProps {
  getValues: UseFormGetValues<IAddPartLineFormData>;
  reset: UseFormReset<IAddPartLineFormData>;
  setPurchaseCost: (cost: number | null) => void;
  setStandardCost: (cost: number | null) => void;
  setUomOptions: (uoms: IUom[]) => void;
  setValue: UseFormSetValue<IAddPartLineFormData>;
  setVerifiedPart: (verified: boolean) => void;
  watch: UseFormWatch<IAddPartLineFormData>;
}

export const usePartValidation = ({
  setValue,
  getValues,
  reset,
  watch,
  setUomOptions,
  setVerifiedPart,
  setStandardCost,
  setPurchaseCost,
}: IProps) => {
  const [isValidating, setIsValidating] = useState(false);

  const { setFocusElementName } = useFocusInputFunctionality();

  const { validateComponentTemplatePart } = useComponentTemplateActions();

  const onValidatePart = async (
    part: Partial<IPart> & IAutocompleteOption,
    manufacturer?: IManufacturer
  ) => {
    if (!part?.number) {
      return;
    }

    setIsValidating(true);

    const { success, record: validatedPartResponse } = await validateComponentTemplatePart(
      {
        number: part.number,
        manufacturerId: manufacturer?.id || part.manufacturerId || null,
      },
      {
        onSuccess: () => setIsValidating(false),
        onError: () => setIsValidating(false),
      }
    );

    if (!success) {
      setValue('part', null);
      setValue('manufacturer', null);
      setValue('description', null);

      setUomOptions([]);
      setVerifiedPart(false);

      return;
    }

    const previousPart = getValues('part');
    const previousManufacturer = getValues('manufacturer');
    const previousDescription = getValues('description');

    const { part: validatedPart, uoms } = validatedPartResponse || {};

    setValue('part', {
      ...previousPart,
      description: validatedPart?.description,
      descriptionLong: validatedPart?.descriptionLong,
      manufacturerId: validatedPart?.manufacturerId,
      manufacturerName: validatedPart?.manufacturerName,
      number: PartNumberHelper.toLabel(validatedPart?.number || previousPart?.number),
      verified: validatedPart?.verified ? 1 : 0,
    });

    setValue(
      'manufacturer',
      validatedPart?.manufacturerId
        ? {
            id: validatedPart.manufacturerId,
            name: validatedPart.manufacturerName,
          }
        : previousManufacturer
    );
    setValue(
      'description',
      validatedPart?.description || validatedPart?.descriptionLong || previousDescription
    );

    setValue('uom', uoms?.[0]);
    setUomOptions(uoms?.map((uom: string) => ({ id: uom, name: uom })));

    setVerifiedPart(Boolean(validatedPart?.verified));

    const pricesUom = validatedPartResponse?.prices?.[watch('uom')];

    setStandardCost(pricesUom?.cost);
    setPurchaseCost(pricesUom?.purchaseCost);

    setFocusElementName(validatedPart?.verified ? 'quantity' : 'manufacturer');
  };

  const onChangePartNumber = (part: Partial<IPart> & IAutocompleteOption) => {
    if (!part) {
      reset();

      setVerifiedPart(false);

      return;
    }

    onValidatePart(part, getValues('manufacturer'));
  };

  const onKeyDownPartNumber = (event: KeyboardEvent<HTMLInputElement>, value: string) => {
    if (isEnterKeyPress(event) || isTabKeyPress(event)) {
      event.preventDefault();

      const part = { number: value };

      setValue('part', part);

      onChangePartNumber(part);
    }
  };

  const onChangeManufacturer = (manufacturer: IManufacturer) => {
    const partValue = getValues('part');

    if (!manufacturer && !partValue) {
      return;
    }

    onValidatePart(partValue, manufacturer);
  };

  return {
    isValidating,
    onChangeManufacturer,
    onChangePartNumber,
    onKeyDownPartNumber,
  };
};
